/**
 * MentionTextArea — textarea with @mention autocomplete for project members.
 *
 * When the user types '@', a dropdown of project members appears. Selecting
 * a member inserts @[Display Name](user_id) into the text. The dropdown
 * filters by display name and email prefix match.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Box, TextArea } from "@radix-ui/themes";
import { useMembers } from "../../hooks/useMembers";
import type { ProjectMember } from "../../api/members";

interface MentionTextAreaProps {
  projectId: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onSubmit?: () => void;
}

export default function MentionTextArea({
  projectId,
  value,
  onChange,
  placeholder,
  onSubmit,
}: MentionTextAreaProps) {
  const { data: members } = useMembers(projectId);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [mentionState, setMentionState] = useState<{
    active: boolean;
    startIndex: number;
    query: string;
    selectedIdx: number;
  }>({ active: false, startIndex: 0, query: "", selectedIdx: 0 });

  const filtered = mentionState.active
    ? (members ?? []).filter((m) => {
        const q = mentionState.query.toLowerCase();
        const name = (m.display_name ?? "").toLowerCase();
        const email = (m.email ?? "").toLowerCase();
        return name.includes(q) || email.includes(q);
      }).slice(0, 8)
    : [];

  const insertMention = useCallback(
    (member: ProjectMember) => {
      const label = member.display_name || member.email || member.user_id.slice(0, 8);
      const mention = `@[${label}](${member.user_id})`;
      const before = value.slice(0, mentionState.startIndex);
      const after = value.slice(mentionState.startIndex + mentionState.query.length + 1); // +1 for '@'
      const next = before + mention + " " + after;
      onChange(next);
      setMentionState({ active: false, startIndex: 0, query: "", selectedIdx: 0 });

      // Restore focus
      requestAnimationFrame(() => {
        const ta = textareaRef.current;
        if (ta) {
          const cursor = before.length + mention.length + 1;
          ta.focus();
          ta.setSelectionRange(cursor, cursor);
        }
      });
    },
    [value, mentionState, onChange],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const text = e.target.value;
      onChange(text);

      const cursorPos = e.target.selectionStart ?? text.length;
      // Look backwards for an unmatched '@'
      const before = text.slice(0, cursorPos);
      const atIdx = before.lastIndexOf("@");

      if (atIdx >= 0) {
        const charBefore = atIdx > 0 ? before[atIdx - 1] : " ";
        const query = before.slice(atIdx + 1);
        // Only activate if @ is at start or preceded by whitespace, and query has no spaces
        if ((charBefore === " " || charBefore === "\n" || atIdx === 0) && !query.includes(" ")) {
          setMentionState({ active: true, startIndex: atIdx, query, selectedIdx: 0 });
          return;
        }
      }
      setMentionState((prev) => (prev.active ? { ...prev, active: false } : prev));
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (mentionState.active && filtered.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setMentionState((s) => ({ ...s, selectedIdx: Math.min(s.selectedIdx + 1, filtered.length - 1) }));
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setMentionState((s) => ({ ...s, selectedIdx: Math.max(s.selectedIdx - 1, 0) }));
          return;
        }
        if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          const selected = filtered[mentionState.selectedIdx];
          if (selected) insertMention(selected);
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setMentionState({ active: false, startIndex: 0, query: "", selectedIdx: 0 });
          return;
        }
      }

      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onSubmit?.();
      }
    },
    [mentionState, filtered, insertMention, onSubmit],
  );

  // Dismiss dropdown when clicking outside
  useEffect(() => {
    if (!mentionState.active) return;
    const dismiss = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setMentionState((s) => ({ ...s, active: false }));
      }
    };
    document.addEventListener("mousedown", dismiss);
    return () => document.removeEventListener("mousedown", dismiss);
  }, [mentionState.active]);

  return (
    <Box style={{ position: "relative" }}>
      <TextArea
        ref={textareaRef}
        size="1"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        style={{ minHeight: 60 }}
      />
      {mentionState.active && filtered.length > 0 && (
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            bottom: "100%",
            left: 0,
            marginBottom: 4,
            width: 240,
            maxHeight: 200,
            overflowY: "auto",
            background: "var(--color-surface)",
            border: "1px solid var(--gray-a5)",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 100,
            padding: 4,
          }}
        >
          {filtered.map((m, i) => (
            <button
              key={m.user_id}
              onClick={() => insertMention(m)}
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                padding: "6px 8px",
                border: "none",
                borderRadius: 6,
                background: i === mentionState.selectedIdx ? "var(--blue-a3)" : "transparent",
                cursor: "pointer",
                textAlign: "left",
                fontSize: 13,
                color: "var(--gray-12)",
              }}
            >
              <span style={{ fontWeight: 500 }}>
                {m.display_name || m.email || m.user_id.slice(0, 8)}
              </span>
              {m.email && m.display_name && (
                <span style={{ fontSize: 11, color: "var(--gray-9)" }}>{m.email}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </Box>
  );
}
